import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import ProductManagement from './ProductManagement';
import CouponManagement from './CouponManagement';

function isKitDesignerOrder(order) {
  if (!order) return false;
  if (order.customization?.source === 'kitDesigner3d') return true;
  const n = String(order.product_name || '');
  return n.includes('Custom Kit') && n.includes('3D');
}

/** Columns only — excludes huge preview/base64/JSON so list queries stay under statement timeout */
const ORDERS_LIST_COLUMNS = [
  'id',
  'order_number',
  'user_id',
  'customer_name',
  'customer_email',
  'customer_phone',
  'shipping_address',
  'product_id',
  'product_name',
  'product_image',
  'product_price',
  'size',
  'custom_size',
  'quantity',
  'amount',
  'status',
  'description',
  'created_at',
  'updated_at',
].join(',');

const LOGO_ZONE_LABELS = {
  chest: 'Chest (front)',
  back: 'Back',
  sleeveLeft: 'Left sleeve',
  sleeveRight: 'Right sleeve',
};

const KIT_VIEW_ORDER = [
  { key: 'front', label: 'Front' },
  { key: 'back', label: 'Back' },
  { key: 'sleeveLeft', label: 'Left sleeve' },
  { key: 'sleeveRight', label: 'Right sleeve' },
];

/** Matches buildKitOrderCompositeImage in KitDesigner3D: TL front, TR back, BL left sleeve, BR right sleeve */
const COMPOSITE_BG_POSITIONS = ['0% 0%', '100% 0%', '0% 100%', '100% 100%'];

function sanitizeFilenamePart(s) {
  return String(s || 'order').replace(/[^a-z0-9._-]+/gi, '_');
}

function orderAssetPrefix(order) {
  const num =
    order?.order_number != null ? String(order.order_number) : String(order?.id || 'order').slice(0, 12);
  return `order-${sanitizeFilenamePart(num)}`;
}

function extFromDataUrl(dataUrl) {
  if (!dataUrl || typeof dataUrl !== 'string') return 'png';
  if (dataUrl.startsWith('data:image/jpeg')) return 'jpg';
  if (dataUrl.startsWith('data:image/webp')) return 'webp';
  if (dataUrl.startsWith('data:image/gif')) return 'gif';
  return 'png';
}

function downloadDataUrl(dataUrl, filename) {
  if (!dataUrl || typeof dataUrl !== 'string') return;
  const safe = sanitizeFilenamePart(filename.replace(/\.[a-z0-9]+$/i, ''));
  const extMatch = filename.match(/\.([a-z0-9]+)$/i);
  const ext = extMatch ? extMatch[1].toLowerCase() : extFromDataUrl(dataUrl);
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = `${safe}.${ext}`;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed'));
    img.src = src;
  });
}

/** Crop one quadrant from the 2×2 composite (same layout as KitDesigner3D buildKitOrderCompositeImage). */
async function dataUrlFromCompositeQuadrant(compositeSrc, quadrantIndex) {
  const img = await loadImageElement(compositeSrc);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const qw = Math.floor(w / 2);
  const qh = Math.floor(h / 2);
  const origins = [
    [0, 0],
    [qw, 0],
    [0, qh],
    [qw, qh],
  ];
  const [sx, sy] = origins[quadrantIndex] || [0, 0];
  const canvas = document.createElement('canvas');
  canvas.width = qw;
  canvas.height = qh;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unsupported');
  ctx.drawImage(img, sx, sy, qw, qh, 0, 0, qw, qh);
  return canvas.toDataURL('image/png');
}

function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function KitAssetDownloadButton({ label, disabled, onClick }) {
  return (
    <button
      type="button"
      className="kit-asset-download-btn"
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );
}

function KitCompositeQuadrantGrid({ src, filePrefix }) {
  const [busy, setBusy] = useState(false);

  const downloadQuadrant = async (i, key) => {
    if (!src || busy) return;
    setBusy(true);
    try {
      const dataUrl = await dataUrlFromCompositeQuadrant(src, i);
      downloadDataUrl(dataUrl, `${filePrefix}-shirt-${key}.png`);
    } catch (e) {
      console.error('[Admin] Quadrant download', e);
    } finally {
      setBusy(false);
    }
  };

  const downloadAllShirtViews = async () => {
    if (!src || busy) return;
    setBusy(true);
    try {
      for (let i = 0; i < KIT_VIEW_ORDER.length; i += 1) {
        const { key } = KIT_VIEW_ORDER[i];
        try {
          const dataUrl = await dataUrlFromCompositeQuadrant(src, i);
          downloadDataUrl(dataUrl, `${filePrefix}-shirt-${key}.png`);
          await delay(350);
        } catch (e) {
          console.error('[Admin] Batch quadrant', e);
        }
      }
    } finally {
      setBusy(false);
    }
  };

  if (!src) return null;
  return (
    <>
      <div className="kit-fulfillment-download-bar">
        <KitAssetDownloadButton
          label="Download all shirt views (4 files)"
          disabled={busy}
          onClick={downloadAllShirtViews}
        />
      </div>
      <div className="kit-fulfillment-grid">
        {KIT_VIEW_ORDER.map(({ key, label }, i) => (
          <div key={key} className="kit-fulfillment-cell">
            <div className="kit-fulfillment-cell-head">
              <span className="kit-fulfillment-cell-label">{label}</span>
              <KitAssetDownloadButton
                label="Download"
                disabled={busy}
                onClick={() => downloadQuadrant(i, key)}
              />
            </div>
            <div className="kit-fulfillment-quadrant-wrap">
              <div
                className="kit-fulfillment-quadrant-bg"
                style={{
                  backgroundImage: `url(${src})`,
                  backgroundSize: '200% 200%',
                  backgroundPosition: COMPOSITE_BG_POSITIONS[i],
                  backgroundRepeat: 'no-repeat',
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function KitViewImagesGrid({ viewImages, filePrefix }) {
  if (!viewImages) return null;

  const downloadView = (key, url) => {
    if (!url) return;
    downloadDataUrl(url, `${filePrefix}-shirt-${key}.${extFromDataUrl(url)}`);
  };

  const downloadAllShirtViews = async () => {
    for (const { key } of KIT_VIEW_ORDER) {
      const url = viewImages[key];
      if (url) {
        downloadDataUrl(url, `${filePrefix}-shirt-${key}.${extFromDataUrl(url)}`);
        await delay(350);
      }
    }
  };

  const anyView = KIT_VIEW_ORDER.some(({ key }) => viewImages[key]);

  return (
    <>
      {anyView && (
        <div className="kit-fulfillment-download-bar">
          <KitAssetDownloadButton label="Download all shirt views (4 files)" onClick={downloadAllShirtViews} />
        </div>
      )}
      <div className="kit-fulfillment-grid">
        {KIT_VIEW_ORDER.map(({ key, label }) => {
          const url = viewImages[key];
          return (
            <div key={key} className="kit-fulfillment-cell">
              <div className="kit-fulfillment-cell-head">
                <span className="kit-fulfillment-cell-label">{label}</span>
                {url ? (
                  <KitAssetDownloadButton
                    label="Download"
                    onClick={() => downloadView(key, url)}
                  />
                ) : (
                  <span className="kit-fulfillment-no-dl">—</span>
                )}
              </div>
              <div className="kit-fulfillment-single-img-wrap">
                {url ? (
                  <img src={url} alt={label} className="kit-fulfillment-single-img" />
                ) : (
                  <div className="kit-fulfillment-missing">No capture for this view</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

/** Large per-view or enlarged quadrants so back/sleeves are readable for fulfillment */
function KitFulfillmentPreview({ order }) {
  const filePrefix = orderAssetPrefix(order);
  const kd = order?.customization?.kitDesign;
  const viewImages = kd?.viewImages;
  const hasAnyView =
    viewImages &&
    (viewImages.front || viewImages.back || viewImages.sleeveLeft || viewImages.sleeveRight);

  if (hasAnyView) {
    return (
      <>
        <p className="order-design-preview-caption">
          Full-size renders from the 3D designer — use these to verify text, logos, and colors on each area.
          Download each view or all four shirt images below.
        </p>
        <KitViewImagesGrid viewImages={viewImages} filePrefix={filePrefix} />
      </>
    );
  }

  if (order?.preview_image) {
    return (
      <>
        <p className="order-design-preview-caption">
          Enlarged panels from the 2×2 order sheet (same image as checkout). Layout: top-left front, top-right
          back, bottom-left left sleeve, bottom-right right sleeve. Use Download on each panel or download all
          four separated files.
        </p>
        <KitCompositeQuadrantGrid src={order.preview_image} filePrefix={filePrefix} />
        <details className="kit-full-sheet-details">
          <summary>Full sheet image (reference)</summary>
          <img
            src={order.preview_image}
            alt="Full order preview sheet"
            className="kit-full-sheet-img"
          />
          <div className="kit-full-sheet-actions">
            <KitAssetDownloadButton
              label="Download full sheet (2×2)"
              onClick={() =>
                downloadDataUrl(order.preview_image, `${filePrefix}-shirt-full-composite.png`)
              }
            />
          </div>
        </details>
      </>
    );
  }

  return (
    <p className="order-design-preview-caption">
      No preview image on file (order may have hit storage limits). Use the breakdown below if design data was
      saved.
    </p>
  );
}

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerStats, setCustomerStats] = useState({
    totalCustomers: 0,
    newThisMonth: 0,
    activeCustomers: 0
  });
  const [stats, setStats] = useState({
    totalOrders: 0,
    totalRevenue: 0,
    pendingOrders: 0,
    completedOrders: 0
  });
  const [loading, setLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const [orderDetailLoading, setOrderDetailLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(ORDERS_LIST_COLUMNS)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      setOrders(data || []);
    } catch (err) {
      console.error('Error fetching orders:', err);
      const msg = err?.message || String(err);
      const timeoutHint = msg.toLowerCase().includes('timeout')
        ? ' List load skips large image/JSON fields; if this persists, reduce data in older rows or archive orders.'
        : '';
      const rlsHint =
        !msg.toLowerCase().includes('timeout') &&
        (msg.includes('permission') || msg.includes('policy') || msg.includes('RLS'))
          ? ' In Supabase SQL Editor run RUN_IN_SUPABASE_ORDERS_ADMIN_FIX.sql (RLS).'
          : '';
      setError(`Failed to load orders: ${msg}.${timeoutHint}${rlsHint}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      console.log('Fetching stats from Supabase...');
      const { data, error } = await supabase
        .from('orders')
        .select('amount, status');

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.log('No orders found in database');
        setStats({
          totalOrders: 0,
          totalRevenue: '0.00',
          pendingOrders: 0,
          completedOrders: 0
        });
        return;
      }

      const totalRevenue = data.reduce((sum, order) => sum + parseFloat(order.amount || 0), 0);
      const pendingOrders = data.filter(o => o.status === 'Pending').length;
      const completedOrders = data.filter(o => o.status === 'Completed').length;

      console.log('Stats calculated:', { totalOrders: data.length, totalRevenue });

      setStats({
        totalOrders: data.length,
        totalRevenue: totalRevenue.toFixed(2),
        pendingOrders,
        completedOrders
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'orders' || activeTab === 'overview') {
      fetchOrders();
      fetchStats();
    }
  }, [activeTab, fetchOrders, fetchStats]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        fetchOrders();
        fetchStats();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [fetchOrders, fetchStats]);

  // Fetch customers when tab is active
  useEffect(() => {
    if (activeTab === 'customers') {
      fetchCustomers();
    }
  }, [activeTab]);

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      // Get unique customers from orders
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(
          'customer_name, customer_email, customer_phone, created_at, amount, status'
        )
        .limit(5000);

      if (ordersError) throw ordersError;

      // Group by email to get unique customers
      const customerMap = {};
      ordersData.forEach(order => {
        const email = order.customer_email;
        if (!customerMap[email]) {
          customerMap[email] = {
            name: order.customer_name,
            email: order.customer_email,
            phone: order.customer_phone,
            firstOrder: order.created_at,
            totalOrders: 0,
            totalSpent: 0,
            lastOrder: order.created_at,
            status: 'Active'
          };
        }
        customerMap[email].totalOrders++;
        customerMap[email].totalSpent += parseFloat(order.amount);
        if (new Date(order.created_at) > new Date(customerMap[email].lastOrder)) {
          customerMap[email].lastOrder = order.created_at;
        }
      });

      const customersArray = Object.values(customerMap).sort((a, b) => 
        new Date(b.lastOrder) - new Date(a.lastOrder)
      );

      setCustomers(customersArray);

      // Calculate customer stats
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const newThisMonth = customersArray.filter(c => 
        new Date(c.firstOrder) >= thisMonth
      ).length;
      const activeCustomers = customersArray.filter(c => {
        const lastOrderDate = new Date(c.lastOrder);
        const daysSinceLastOrder = (now - lastOrderDate) / (1000 * 60 * 60 * 24);
        return daysSinceLastOrder <= 30;
      }).length;

      setCustomerStats({
        totalCustomers: customersArray.length,
        newThisMonth,
        activeCustomers
      });
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const viewCustomerDetails = async (customer) => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(ORDERS_LIST_COLUMNS)
        .eq('customer_email', customer.email)
        .order('created_at', { ascending: false })
        .limit(200);

      if (error) throw error;

      setSelectedCustomer({
        ...customer,
        orders: data,
      });
    } catch (error) {
      console.error('Error fetching customer details:', error);
    }
  };

  const closeCustomerDetails = () => {
    setSelectedCustomer(null);
  };

  const handleResetAllOrders = async () => {
    const confirmed = window.confirm(
      '⚠️ WARNING: This will permanently delete ALL orders from the database!\n\n' +
      'This action cannot be undone.\n\n' +
      'Are you sure you want to continue?'
    );

    if (!confirmed) return;

    // Double confirmation
    const doubleConfirm = window.confirm(
      '🚨 FINAL CONFIRMATION\n\n' +
      'You are about to delete ALL orders permanently.\n\n' +
      'Click OK to proceed or Cancel to abort.'
    );

    if (!doubleConfirm) return;

    try {
      setLoading(true);

      // Delete in batches of 10 to avoid statement timeout on large rows
      let totalDeleted = 0;
      let hasMore = true;
      while (hasMore) {
        const { data: batch, error: fetchErr } = await supabase
          .from('orders')
          .select('id')
          .limit(10);

        if (fetchErr) throw fetchErr;
        if (!batch || batch.length === 0) { hasMore = false; break; }

        const ids = batch.map((r) => r.id);
        const { error: delErr } = await supabase
          .from('orders')
          .delete()
          .in('id', ids);

        if (delErr) throw delErr;
        totalDeleted += ids.length;
        if (ids.length < 10) hasMore = false;
      }

      // Reset local state
      setOrders([]);
      setStats({
        totalOrders: 0,
        totalRevenue: '0.00',
        pendingOrders: 0,
        completedOrders: 0
      });
      setCustomers([]);
      setCustomerStats({
        totalCustomers: 0,
        newThisMonth: 0,
        activeCustomers: 0
      });

      await fetchOrders();
      await fetchStats();

      alert(`✅ All orders have been deleted successfully! (${totalDeleted} removed)`);
    } catch (error) {
      console.error('Error deleting orders:', error);
      alert(
        '❌ Failed to delete orders: ' + error.message
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminLoggedIn');
    localStorage.removeItem('adminData');
    onLogout();
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: newStatus, updated_at: new Date() })
        .eq('id', orderId);

      if (error) throw error;

      // Refresh orders
      fetchOrders();
      fetchStats();
      alert('Order status updated successfully!');
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update order status');
    }
  };

  const viewOrderDetails = async (order) => {
    if (!order?.id) {
      setSelectedOrder(order);
      return;
    }
    setOrderDetailLoading(true);
    setSelectedOrder(order);
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', order.id)
        .single();

      if (error) throw error;
      if (data) setSelectedOrder(data);
    } catch (e) {
      console.error('Error loading full order:', e);
      setError(
        (e?.message || 'Could not load full order') +
          ' — Open “View” again; one row may still be too large for your project limits.'
      );
    } finally {
      setOrderDetailLoading(false);
    }
  };

  const closeOrderDetails = () => {
    setSelectedOrder(null);
    setOrderDetailLoading(false);
  };

  return (
    <div className={`admin-dashboard${sidebarOpen ? ' admin-sidebar-open' : ''}`}>
      {/* Mobile backdrop */}
      {sidebarOpen && (
        <div className="admin-sidebar-backdrop" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div className="admin-sidebar">
        <div className="admin-logo">
          <h2>Admin Panel</h2>
          <p>Universal Sports Lab</p>
        </div>
        
        <nav className="admin-nav">
          <button 
            className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => { setActiveTab('overview'); setSidebarOpen(false); }}
          >
            📊 Overview
          </button>
          <button 
            className={`nav-item ${activeTab === 'orders' ? 'active' : ''}`}
            onClick={() => { setActiveTab('orders'); setSidebarOpen(false); }}
          >
            🛒 Orders
          </button>
          <button 
            className={`nav-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => { setActiveTab('products'); setSidebarOpen(false); }}
          >
            📦 Products
          </button>
          <button 
            className={`nav-item ${activeTab === 'customers' ? 'active' : ''}`}
            onClick={() => { setActiveTab('customers'); setSidebarOpen(false); }}
          >
            👥 Customers
          </button>
          <button 
            className={`nav-item ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => { setActiveTab('coupons'); setSidebarOpen(false); }}
          >
            🎟️ Coupons
          </button>
        </nav>
        
        <button className="logout-button" onClick={handleLogout}>
          🚪 Logout
        </button>
      </div>

      {/* Main Content */}
      <div className="admin-content">
        <div className="admin-header">
          <div className="admin-header-left">
            <button
              className="admin-hamburger"
              aria-label="Toggle menu"
              onClick={() => setSidebarOpen(o => !o)}
            >
              <span /><span /><span />
            </button>
            <h1>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h1>
          </div>
          <div className="admin-header-actions">
            <button 
              className="add-button add-button--refresh" 
              onClick={() => {
                fetchOrders();
                fetchStats();
                if (activeTab === 'customers') fetchCustomers();
              }}
            >
              🔄 Refresh
            </button>
            <button 
              className="add-button add-button--danger" 
              onClick={handleResetAllOrders}
            >
              🗑️ Reset Orders
            </button>
            <div className="admin-user">
              <span>Admin</span>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#fee',
            color: '#c33',
            padding: '1rem',
            borderRadius: '12px',
            marginBottom: '1.5rem',
            borderLeft: '4px solid #c33'
          }}>
            {error}
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="overview-content">
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">💰</div>
                <div className="stat-info">
                  <h3>£{stats.totalRevenue}</h3>
                  <p>Total Revenue</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🛍️</div>
                <div className="stat-info">
                  <h3>{stats.totalOrders}</h3>
                  <p>Total Orders</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">⏳</div>
                <div className="stat-info">
                  <h3>{stats.pendingOrders}</h3>
                  <p>Pending Orders</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <h3>{stats.completedOrders}</h3>
                  <p>Completed Orders</p>
                </div>
              </div>
            </div>

            <div className="recent-orders">
              <h2>Recent Orders</h2>
              {loading ? (
                <div className="loading-spinner">
                  <div className="spinner"></div>
                </div>
              ) : orders.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📦</div>
                  <h3>No Orders Yet</h3>
                  <p>Orders will appear here once customers start placing them.</p>
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Product</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.slice(0, 10).map(order => (
                      <tr key={order.id}>
                        <td data-label="Order #">
                          #{order.order_number != null ? order.order_number : String(order.id || '').slice(0, 8)}
                        </td>
                        <td data-label="Customer">{order.customer_name}</td>
                        <td data-label="Product">
                          {order.product_name}
                          {isKitDesignerOrder(order) ? (
                            <span className="status-badge pending" style={{ marginLeft: '0.35rem' }}>
                              3D Kit
                            </span>
                          ) : null}
                        </td>
                        <td data-label="Amount">£{order.amount}</td>
                        <td data-label="Status"><span className={`status-badge ${order.status.toLowerCase()}`}>{order.status}</span></td>
                        <td data-label="Date">{new Date(order.created_at).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="action-btn view"
                            onClick={() => viewOrderDetails(order)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="orders-content">
            <h2>All Orders</h2>
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : orders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🛒</div>
                <h3>No Orders Found</h3>
                <p>Your order list is empty. Orders will appear here once placed.</p>
              </div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Order #</th>
                    <th>Customer</th>
                    <th>Email</th>
                    <th>Product / type</th>
                    <th>Size</th>
                    <th>Qty</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(order => (
                    <tr key={order.id}>
                      <td data-label="Order #">
                        #{order.order_number != null ? order.order_number : String(order.id || '').slice(0, 8)}
                      </td>
                      <td data-label="Customer">{order.customer_name}</td>
                      <td data-label="Email">{order.customer_email}</td>
                      <td data-label="Product">
                        {order.product_name}
                        {isKitDesignerOrder(order) ? (
                          <span className="status-badge pending" style={{ marginLeft: '0.35rem' }}>
                            3D Kit
                          </span>
                        ) : null}
                      </td>
                      <td data-label="Size">{order.size}</td>
                      <td data-label="Qty">{order.quantity}</td>
                      <td data-label="Amount">£{order.amount}</td>
                      <td data-label="Status"><span className={`status-badge ${order.status.toLowerCase()}`}>{order.status}</span></td>
                      <td data-label="Date">{new Date(order.created_at).toLocaleDateString()}</td>
                      <td>
                        <button 
                          className="action-btn view"
                          onClick={() => viewOrderDetails(order)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <ProductManagement />
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="customers-content">
            <div className="content-header">
              <h2>Customer Management</h2>
              <input
                type="text"
                placeholder="Search customers by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>

            {/* Customer Stats */}
            <div className="stats-grid" style={{ marginBottom: '2rem' }}>
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-info">
                  <h3>{customerStats.totalCustomers}</h3>
                  <p>Total Customers</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🆕</div>
                <div className="stat-info">
                  <h3>{customerStats.newThisMonth}</h3>
                  <p>New This Month</p>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✨</div>
                <div className="stat-info">
                  <h3>{customerStats.activeCustomers}</h3>
                  <p>Active Customers</p>
                </div>
              </div>
            </div>

            {customersLoading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : customers.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">👥</div>
                <h3>No Customers Yet</h3>
                <p>Customers will appear here once they place orders.</p>
              </div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Customer Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Total Orders</th>
                    <th>Total Spent</th>
                    <th>First Order</th>
                    <th>Last Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {customers
                    .filter(customer => 
                      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      customer.email.toLowerCase().includes(searchTerm.toLowerCase())
                    )
                    .map((customer, index) => (
                      <tr key={index}>
                        <td data-label="Name">{customer.name}</td>
                        <td data-label="Email">{customer.email}</td>
                        <td data-label="Phone">{customer.phone || 'N/A'}</td>
                        <td data-label="Orders">{customer.totalOrders}</td>
                        <td data-label="Spent">£{customer.totalSpent.toFixed(2)}</td>
                        <td data-label="First Order">{new Date(customer.firstOrder).toLocaleDateString()}</td>
                        <td data-label="Last Order">{new Date(customer.lastOrder).toLocaleDateString()}</td>
                        <td>
                          <button 
                            className="action-btn view"
                            onClick={() => viewCustomerDetails(customer)}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {/* Coupons Tab */}
        {activeTab === 'coupons' && (
          <CouponManagement />
        )}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-overlay" onClick={closeOrderDetails}>
          <div
            className={`modal-content order-detail-modal${
              isKitDesignerOrder(selectedOrder) ? ' order-detail-modal--kit' : ''
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                Order Details #
                {selectedOrder.order_number != null
                  ? selectedOrder.order_number
                  : String(selectedOrder.id || '').slice(0, 8)}
              </h2>
              <button className="close-button" onClick={closeOrderDetails}>×</button>
            </div>
            {orderDetailLoading && (
              <p className="order-detail-loading" style={{ margin: '0 1rem 0.75rem', opacity: 0.85 }}>
                Loading full design, previews, and kit details…
              </p>
            )}
            <div className="modal-body">
              <div
                className={`order-detail-layout${
                  isKitDesignerOrder(selectedOrder) ? ' order-detail-layout--kit' : ''
                }`}
              >
                {/* Left Column - Product Preview */}
                <div className="order-preview-column">
                  <div className="preview-card">
                    <h3>
                      {isKitDesignerOrder(selectedOrder)
                        ? '3D kit — production views'
                        : 'Customized Product Preview'}
                    </h3>
                    {isKitDesignerOrder(selectedOrder) ? (
                      <KitFulfillmentPreview order={selectedOrder} />
                    ) : (
                    <div className="preview-images-grid">
                      {/* Front Preview */}
                      <div className="preview-item">
                        <span className="preview-label">Front</span>
                        {selectedOrder.preview_image ? (
                          <img 
                            src={selectedOrder.preview_image} 
                            alt="Front View" 
                            className="order-preview-image"
                          />
                        ) : selectedOrder.product_image ? (
                          <img 
                            src={selectedOrder.product_image} 
                            alt={selectedOrder.product_name}
                            className="order-preview-image"
                          />
                        ) : (
                          <div className="no-preview-small">
                            <span>👕</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Back Preview */}
                      <div className="preview-item">
                        <span className="preview-label">Back</span>
                        {(selectedOrder.back_preview_image || selectedOrder.preview_image) ? (
                          <img 
                            src={selectedOrder.back_preview_image || selectedOrder.preview_image} 
                            alt="Back View" 
                            className="order-preview-image"
                          />
                        ) : selectedOrder.product_image ? (
                          <img 
                            src={selectedOrder.product_image} 
                            alt="Back View" 
                            className="order-preview-image"
                          />
                        ) : (
                          <div className="no-preview-small">
                            <span>👕</span>
                          </div>
                        )}
                      </div>
                    </div>
                    )}
                    <div className="product-info">
                      <h4>{selectedOrder.product_name}</h4>
                      <p className="product-price">£{selectedOrder.product_price ?? 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Order Details */}
                <div className="order-info-column">
                  {/* Customer Information */}
                  <div className="detail-section">
                    <h3>👤 Customer Information</h3>
                    <div className="detail-grid">
                      <div><strong>Name:</strong> {selectedOrder.customer_name}</div>
                      <div><strong>Email:</strong> {selectedOrder.customer_email}</div>
                      <div><strong>Phone:</strong> {selectedOrder.customer_phone || 'N/A'}</div>
                      <div><strong>Address:</strong> {selectedOrder.shipping_address || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Order Information */}
                  <div className="detail-section">
                    <h3>📋 Order Information</h3>
                    <div className="detail-grid">
                      <div><strong>Order Number:</strong> #{selectedOrder.order_number != null ? selectedOrder.order_number : String(selectedOrder.id || '').slice(0, 8)}</div>
                      <div><strong>Size:</strong> {selectedOrder.size}</div>
                      <div><strong>Quantity:</strong> {selectedOrder.quantity}</div>
                      {selectedOrder.quantity > 0 && (
                        <div><strong>Unit Price:</strong> £{(parseFloat(selectedOrder.amount) / selectedOrder.quantity).toFixed(2)}</div>
                      )}
                      <div><strong>Total Amount:</strong> <span className="total-amount">£{selectedOrder.amount}</span></div>
                      <div><strong>Status:</strong> <span className={`status-badge ${selectedOrder.status.toLowerCase()}`}>{selectedOrder.status}</span></div>
                      <div><strong>Order Date:</strong> {new Date(selectedOrder.created_at).toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Custom Size Details */}
                  {selectedOrder.custom_size && (
                    <div className="detail-section">
                      <h3>📏 Custom Measurements</h3>
                      <div className="detail-grid measurements">
                        <div><strong>Chest:</strong> {selectedOrder.custom_size.chest}"</div>
                        <div><strong>Length:</strong> {selectedOrder.custom_size.length}"</div>
                        <div><strong>Shoulder:</strong> {selectedOrder.custom_size.shoulder}"</div>
                        <div><strong>Sleeve:</strong> {selectedOrder.custom_size.sleeve}"</div>
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  {selectedOrder.description && (
                    <div className="detail-section">
                      <h3>📝 Special Instructions</h3>
                      <p className="description-text">{selectedOrder.description}</p>
                    </div>
                  )}

                  {isKitDesignerOrder(selectedOrder) && selectedOrder.customization?.kitDesign && (
                    <div className="detail-section kit-detail-breakdown">
                      <h3>🎨 3D kit details</h3>
                      <div className="detail-grid">
                        {selectedOrder.customization.kitDesign.designColors && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <strong>Colors:</strong>{' '}
                            {Object.entries(selectedOrder.customization.kitDesign.designColors).map(
                              ([k, v]) => (
                                <span
                                  key={k}
                                  style={{
                                    marginRight: '0.75rem',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                  }}
                                >
                                  {k}:
                                  <span
                                    title={String(v)}
                                    style={{
                                      width: 14,
                                      height: 14,
                                      background: v,
                                      borderRadius: 3,
                                      border: '1px solid rgba(255,255,255,0.25)',
                                    }}
                                  />
                                  <span style={{ fontSize: '0.85rem' }}>{String(v)}</span>
                                </span>
                              )
                            )}
                          </div>
                        )}
                        {selectedOrder.customization.kitDesign.kitOptions && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <strong>Kit Options:</strong>{' '}
                            <span className={`status-badge ${selectedOrder.customization.kitDesign.kitOptions.fullSleeves ? 'completed' : 'cancelled'}`} style={{ marginRight: '0.5rem' }}>
                              Full Sleeves: {selectedOrder.customization.kitDesign.kitOptions.fullSleeves ? '✅ Yes' : '❌ No'}
                            </span>
                            <span className={`status-badge ${selectedOrder.customization.kitDesign.kitOptions.shirtColor ? 'completed' : 'cancelled'}`}>
                              Custom Shirt Color: {selectedOrder.customization.kitDesign.kitOptions.shirtColor ? '✅ Yes' : '❌ No'}
                            </span>
                          </div>
                        )}
                        {selectedOrder.customization.note && (
                          <div style={{ gridColumn: '1 / -1', fontSize: '0.9rem', opacity: 0.9 }}>
                            Note: {selectedOrder.customization.note}
                          </div>
                        )}
                      </div>

                      {Array.isArray(selectedOrder.customization.kitDesign.customLabels) &&
                        selectedOrder.customization.kitDesign.customLabels.length > 0 && (
                        <div className="kit-detail-subsection">
                          <h4>Custom text (back / placement)</h4>
                          <ul className="kit-detail-lines">
                            {selectedOrder.customization.kitDesign.customLabels.map((row, idx) => (
                              <li key={row.id || idx}>
                                <strong>Line {idx + 1}:</strong>{' '}
                                <span className="kit-detail-line-text">
                                  {row.text?.trim() ? row.text : '(empty)'}
                                </span>
                                <span className="kit-detail-meta">
                                  {' '}
                                  · {row.size ?? '?'}px · {row.fontId ?? 'font'} ·{' '}
                                  <span
                                    className="kit-detail-color-swatch"
                                    style={{ background: row.color || '#ccc' }}
                                    title={row.color}
                                  />
                                  {row.color}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {Array.isArray(selectedOrder.customization.kitDesign.customLogos) &&
                        selectedOrder.customization.kitDesign.customLogos.length > 0 && (
                        <div className="kit-detail-subsection kit-detail-logos-section">
                          <h4>Logos — separate files (download for production)</h4>
                          <p className="kit-detail-subsection-hint">
                            Each logo is shown at full stored resolution with its own download.
                          </p>
                          <div className="kit-detail-download-bar">
                            <KitAssetDownloadButton
                              label="Download all logo files"
                              onClick={async () => {
                                const logos = selectedOrder.customization.kitDesign.customLogos;
                                const prefix = orderAssetPrefix(selectedOrder);
                                for (let i = 0; i < logos.length; i += 1) {
                                  const row = logos[i];
                                  if (row.imageDataUrl) {
                                    const zone = sanitizeFilenamePart(row.zone || `logo-${i + 1}`);
                                    downloadDataUrl(
                                      row.imageDataUrl,
                                      `${prefix}-logo-${i + 1}-${zone}.${extFromDataUrl(row.imageDataUrl)}`
                                    );
                                    await delay(350);
                                  }
                                }
                              }}
                            />
                          </div>
                          <ul className="kit-detail-logo-cards">
                            {selectedOrder.customization.kitDesign.customLogos.map((row, idx) => {
                              const prefix = orderAssetPrefix(selectedOrder);
                              const zone = sanitizeFilenamePart(row.zone || `slot-${idx + 1}`);
                              return (
                                <li key={row.id || idx} className="kit-detail-logo-card">
                                  <div className="kit-detail-logo-card-title">
                                    <strong>Logo {idx + 1}</strong>
                                    <span className="kit-detail-logo-zone">
                                      {LOGO_ZONE_LABELS[row.zone] || row.zone || 'Zone'}
                                    </span>
                                    {row.scale != null && (
                                      <span className="kit-detail-meta">
                                        {' '}
                                        · scale {Number(row.scale).toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                  {row.imageDataUrl ? (
                                    <>
                                      <div className="kit-detail-logo-preview-wrap">
                                        <img
                                          src={row.imageDataUrl}
                                          alt={`Logo ${idx + 1} for ${LOGO_ZONE_LABELS[row.zone] || row.zone}`}
                                          className="kit-detail-logo-preview"
                                        />
                                      </div>
                                      <div className="kit-detail-logo-actions">
                                        <KitAssetDownloadButton
                                          label="Download this logo"
                                          onClick={() =>
                                            downloadDataUrl(
                                              row.imageDataUrl,
                                              `${prefix}-logo-${idx + 1}-${zone}.${extFromDataUrl(row.imageDataUrl)}`
                                            )
                                          }
                                        />
                                      </div>
                                    </>
                                  ) : (
                                    <span className="kit-detail-missing">No image data stored for this slot</span>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      <p className="kit-detail-json-hint">
                        Full JSON is stored in <code>customization</code> in your database for production
                        tooling.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Update */}
              <div className="detail-section status-section">
                <h3>⚙️ Update Order Status</h3>
                <div className="status-buttons">
                  <button 
                    className="status-btn pending"
                    onClick={() => handleStatusUpdate(selectedOrder.id, 'Pending')}
                    disabled={selectedOrder.status === 'Pending'}
                  >
                    📋 Pending
                  </button>
                  <button 
                    className="status-btn processing"
                    onClick={() => handleStatusUpdate(selectedOrder.id, 'Processing')}
                    disabled={selectedOrder.status === 'Processing'}
                  >
                    🔄 Processing
                  </button>
                  <button 
                    className="status-btn completed"
                    onClick={() => handleStatusUpdate(selectedOrder.id, 'Completed')}
                    disabled={selectedOrder.status === 'Completed'}
                  >
                    ✅ Completed
                  </button>
                  <button 
                    className="status-btn cancelled"
                    onClick={() => handleStatusUpdate(selectedOrder.id, 'Cancelled')}
                    disabled={selectedOrder.status === 'Cancelled'}
                  >
                    ❌ Cancelled
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {selectedCustomer && (
        <div className="modal-overlay" onClick={closeCustomerDetails}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Customer Details - {selectedCustomer.name}</h2>
              <button className="close-button" onClick={closeCustomerDetails}>×</button>
            </div>
            
            <div className="modal-body">
              {/* Customer Information */}
              <div className="detail-section">
                <h3>Contact Information</h3>
                <div className="detail-grid">
                  <div><strong>Full Name:</strong> {selectedCustomer.name}</div>
                  <div><strong>Email:</strong> {selectedCustomer.email}</div>
                  <div><strong>Phone:</strong> {selectedCustomer.phone || 'Not provided'}</div>
                  <div><strong>Status:</strong> <span className="status-badge active">{selectedCustomer.status}</span></div>
                </div>
              </div>

              {/* Customer Stats */}
              <div className="detail-section">
                <h3>Customer Statistics</h3>
                <div className="detail-grid">
                  <div><strong>Total Orders:</strong> {selectedCustomer.totalOrders}</div>
                  <div><strong>Total Spent:</strong> £{selectedCustomer.totalSpent.toFixed(2)}</div>
                  <div><strong>Average Order:</strong> £{(selectedCustomer.totalSpent / selectedCustomer.totalOrders).toFixed(2)}</div>
                  <div><strong>First Order:</strong> {new Date(selectedCustomer.firstOrder).toLocaleDateString()}</div>
                  <div><strong>Last Order:</strong> {new Date(selectedCustomer.lastOrder).toLocaleDateString()}</div>
                  <div><strong>Customer Since:</strong> {Math.floor((new Date() - new Date(selectedCustomer.firstOrder)) / (1000 * 60 * 60 * 24))} days</div>
                </div>
              </div>

              {/* Order History */}
              <div className="detail-section">
                <h3>Order History ({selectedCustomer.orders?.length || 0} orders)</h3>
                {selectedCustomer.orders && selectedCustomer.orders.length > 0 ? (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Product</th>
                        <th>Size</th>
                        <th>Qty</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCustomer.orders.map(order => (
                        <tr key={order.id}>
                          <td data-label="Order #">#{order.order_number}</td>
                          <td data-label="Product">{order.product_name}</td>
                          <td data-label="Size">{order.size}</td>
                          <td data-label="Qty">{order.quantity}</td>
                          <td data-label="Amount">£{order.amount}</td>
                          <td data-label="Status"><span className={`status-badge ${order.status.toLowerCase()}`}>{order.status}</span></td>
                          <td data-label="Date">{new Date(order.created_at).toLocaleDateString()}</td>
                          <td>
                            <button 
                              className="action-btn view"
                              onClick={() => {
                                closeCustomerDetails();
                                viewOrderDetails(order);
                              }}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p>No orders found for this customer.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
